import { useState, type ReactElement } from 'react'
import {
  AppBar,
  Avatar,
  Badge,
  Box,
  Chip,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Snackbar,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import MenuIcon from '@mui/icons-material/Menu'
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined'
import RuleFolderOutlinedIcon from '@mui/icons-material/RuleFolderOutlined'
import PlaylistAddCheckOutlinedIcon from '@mui/icons-material/PlaylistAddCheckOutlined'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined'
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined'
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined'
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined'
import SpeedOutlinedIcon from '@mui/icons-material/SpeedOutlined'
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined'
import PermMediaOutlinedIcon from '@mui/icons-material/PermMediaOutlined'
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined'
import LogoutIcon from '@mui/icons-material/Logout'
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '@/auth/AuthContext'
import { Marca } from '@/components/Marca'
import themeConfig from '@/configs/themeConfig'
import { navegacaoDo, type ItemNav } from './navegacao'

const NAV_WIDTH = themeConfig.navigationSize

const ICONES: Record<ItemNav['icone'], ReactElement> = {
  auditoria: <FactCheckOutlinedIcon fontSize="small" />,
  fila: <RuleFolderOutlinedIcon fontSize="small" />,
  gestao_nc: <PlaylistAddCheckOutlinedIcon fontSize="small" />,
  ficha: <DescriptionOutlinedIcon fontSize="small" />,
  nc: <ReportProblemOutlinedIcon fontSize="small" />,
  usuario: <PeopleAltOutlinedIcon fontSize="small" />,
  prestadora: <BusinessOutlinedIcon fontSize="small" />,
  dashboard: <InsightsOutlinedIcon fontSize="small" />,
  pressao: <SpeedOutlinedIcon fontSize="small" />,
  publico: <PublicOutlinedIcon fontSize="small" />,
  midia: <PermMediaOutlinedIcon fontSize="small" />,
  auditoria_log: <HistoryOutlinedIcon fontSize="small" />,
}

const ROTULO_PAPEL = { fiscal: 'Fiscal', gerente: 'Gerente', admin: 'Administrador' } as const

function Nav({ aoNavegar }: { aoNavegar?: () => void }) {
  const { usuario } = useAuth()
  const { pathname } = useLocation()
  if (!usuario) return null

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ px: 5, height: 64, display: 'flex', alignItems: 'center', flexShrink: 0 }}>
        <Marca />
      </Box>

      <Box sx={{ overflowY: 'auto', flex: 1, px: 3, pb: 4 }}>
        {navegacaoDo(usuario.papel).map((secao) => (
          <Box key={secao.secao} sx={{ mb: 2 }}>
            <Typography
              variant="caption"
              sx={{ px: 3, py: 2, display: 'block', color: 'text.disabled', fontWeight: 600, letterSpacing: '0.4px' }}
            >
              {secao.secao.toUpperCase()}
            </Typography>
            <List disablePadding>
              {secao.itens.map((item) => {
                const ativo = pathname === item.rota || pathname.startsWith(`${item.rota}/`)
                return (
                  <ListItemButton
                    key={item.rota}
                    component={NavLink}
                    to={item.rota}
                    onClick={aoNavegar}
                    sx={{
                      mb: 1,
                      py: 2,
                      color: ativo ? 'primary.contrastText' : 'text.secondary',
                      // ativo = gradiente do primario, a assinatura do nav da casa
                      background: ativo
                        ? (t) => `linear-gradient(72deg, ${t.palette.primary.main} 0%, ${t.palette.primary.light} 100%)`
                        : 'none',
                      boxShadow: ativo ? (t) => t.shadows[3] : 'none',
                      '&:hover': { bgcolor: ativo ? undefined : 'action.hover' },
                      '&.active': { color: 'primary.contrastText' },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>{ICONES[item.icone]}</ListItemIcon>
                    <ListItemText
                      primary={item.titulo}
                      primaryTypographyProps={{ fontSize: '0.9375rem', fontWeight: ativo ? 500 : 400 }}
                    />
                  </ListItemButton>
                )
              })}
            </List>
          </Box>
        ))}
      </Box>
    </Box>
  )
}

export default function DashboardLayout() {
  const theme = useTheme()
  const desktop = useMediaQuery(theme.breakpoints.up('lg'))
  const [aberto, setAberto] = useState(false)
  const [menu, setMenu] = useState<HTMLElement | null>(null)
  const [aviso, setAviso] = useState<string | null>(null)
  const { usuario, sair } = useAuth()
  const navigate = useNavigate()

  const iniciais = usuario?.nome
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0])
    .join('')
    .toUpperCase()

  const emBreve = (recurso: string) => {
    setMenu(null)
    setAviso(`${recurso} em breve.`)
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <Drawer
        variant={desktop ? 'permanent' : 'temporary'}
        open={desktop || aberto}
        onClose={() => setAberto(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          width: NAV_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': { width: NAV_WIDTH, borderRight: 'none', boxShadow: theme.shadows[6] },
        }}
      >
        <Nav aoNavegar={desktop ? undefined : () => setAberto(false)} />
      </Drawer>

      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <AppBar
          position="sticky"
          elevation={0}
          color="transparent"
          sx={{
            backdropFilter: 'blur(6px)',
            bgcolor: (t) => alpha(t.palette.customColors.bodyBg, 0.9),
          }}
        >
          <Toolbar sx={{ gap: 2 }}>
            {!desktop && (
              <IconButton edge="start" onClick={() => setAberto(true)} aria-label="Abrir menu">
                <MenuIcon />
              </IconButton>
            )}
            <Box sx={{ flex: 1 }} />
            {usuario && (
              <>
                <Chip size="small" variant="outlined" label={ROTULO_PAPEL[usuario.papel]} />
                <IconButton onClick={(e) => setMenu(e.currentTarget)} aria-label="Conta" sx={{ p: 0.5 }}>
                  <Badge
                    overlap="circular"
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    badgeContent={
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          bgcolor: 'success.main',
                          boxShadow: (t) => `0 0 0 2px ${t.palette.background.paper}`,
                        }}
                      />
                    }
                  >
                    <Avatar sx={{ width: 38, height: 38, bgcolor: 'primary.main', fontSize: '0.875rem' }}>
                      {iniciais}
                    </Avatar>
                  </Badge>
                </IconButton>
                <Menu
                  anchorEl={menu}
                  open={!!menu}
                  onClose={() => setMenu(null)}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  slotProps={{ paper: { sx: { minWidth: 240, mt: 1 } } }}
                >
                  <Box sx={{ px: 4, py: 3, display: 'flex', alignItems: 'center', gap: 3 }}>
                    <Badge
                      overlap="circular"
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                      badgeContent={
                        <Box
                          sx={{
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            bgcolor: 'success.main',
                            boxShadow: (t) => `0 0 0 2px ${t.palette.background.paper}`,
                          }}
                        />
                      }
                    >
                      <Avatar sx={{ width: 40, height: 40, bgcolor: 'primary.main', fontSize: '0.9375rem' }}>
                        {iniciais}
                      </Avatar>
                    </Badge>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600} noWrap>
                        {usuario.nome}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                        {ROTULO_PAPEL[usuario.papel]}
                      </Typography>
                    </Box>
                  </Box>
                  <Divider />
                  <MenuItem onClick={() => emBreve('Configurações')}>
                    <ListItemIcon>
                      <SettingsOutlinedIcon fontSize="small" />
                    </ListItemIcon>
                    Configurações
                  </MenuItem>
                  <MenuItem onClick={() => emBreve('Trocar senha')}>
                    <ListItemIcon>
                      <LockOutlinedIcon fontSize="small" />
                    </ListItemIcon>
                    Trocar senha
                  </MenuItem>
                  <Divider />
                  <MenuItem
                    onClick={() => {
                      setMenu(null)
                      sair()
                      navigate('/login', { replace: true })
                    }}
                  >
                    <ListItemIcon>
                      <LogoutIcon fontSize="small" />
                    </ListItemIcon>
                    Sair
                  </MenuItem>
                </Menu>
              </>
            )}
          </Toolbar>
        </AppBar>

        <Box component="main" sx={{ flex: 1, p: { xs: 4, md: 6 }, maxWidth: 1440, width: '100%', mx: 'auto' }}>
          <Outlet />
        </Box>

        <Box
          component="footer"
          sx={{
            px: { xs: 4, md: 6 },
            py: 3,
            textAlign: 'center',
            color: 'text.disabled',
          }}
        >
          <Typography variant="caption">ARSAL · Governo de Alagoas</Typography>
        </Box>
      </Box>

      <Snackbar
        open={!!aviso}
        autoHideDuration={2600}
        onClose={() => setAviso(null)}
        message={aviso ?? ''}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  )
}
